import type { VisitDto } from "@repo/data-store";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui/components/ui/alert-dialog";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { differenceInCalendarDays } from "date-fns";
import { AlertTriangleIcon, ClockIcon, SquareArrowOutUpRight, StethoscopeIcon, UserRoundIcon, X } from "lucide-react";
import { useState } from "react";
import { useSessionPanelActions, useSessionPanelOpen } from "@/app/providers/use-session-panel";
import { getAppointmentActionStates } from "@/features/scheduling/lib/schedule/appointment-time-validation";
import { PractitionerDisplay } from "@/shared/common/practitioner-display";
import { useNow } from "@/shared/lib/clock-store";
import { formatTime } from "@/shared/lib/date-formats";
import { AppointmentStatusBadge } from "@/shared/status/appointment-status-badge";

interface SessionPanelProps {
  visits: VisitDto[];
  completionReasons: ReadonlyMap<string, string | undefined>;
  onCancel: (id: string) => void;
  onComplete: (id: string) => void;
}

export function SessionPanel({ visits, completionReasons, onCancel, onComplete }: SessionPanelProps) {
  const panelOpen = useSessionPanelOpen();
  const { togglePanel } = useSessionPanelActions();
  const [cancelId, setCancelId] = useState<string | null>(null);

  return (
    <div
      className={cn(
        "bg-background overflow-hidden transition-[width,border-width] duration-200 ease-in-out",
        panelOpen ? "w-70 border-l" : "w-0 border-l-0",
      )}
    >
      <div className="w-70 h-full flex flex-col">
        <div className="flex items-center justify-between px-4 py-3">
          <h3 className="text-sm font-semibold">Visits In-Progress</h3>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={togglePanel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-auto p-3">
          {visits.length === 0 ? (
            <p className="text-muted-foreground text-sm">No in-progress visits</p>
          ) : (
            <div className="flex flex-col gap-3">
              {visits.map((visit) => (
                <InProgressCard
                  key={visit.visitId}
                  visit={visit}
                  completionReason={completionReasons.get(visit.visitId)}
                  onCancel={() => setCancelId(visit.visitId)}
                  onComplete={onComplete}
                  onClosePanel={togglePanel}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!cancelId} onOpenChange={(open) => !open && setCancelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Appointment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this appointment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Appointment</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!cancelId) return;
                onCancel(cancelId);
                setCancelId(null);
              }}
            >
              Cancel Appointment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InProgressCard({
  visit,
  completionReason,
  onCancel,
  onComplete,
  onClosePanel,
}: {
  visit: VisitDto;
  completionReason: string | undefined;
  onCancel: () => void;
  onComplete: (id: string) => void;
  onClosePanel: () => void;
}) {
  const now = useNow();
  const { isOverdue, overdueMinutes, complete } = getAppointmentActionStates(visit, now);
  const disabledReason = completionReason ?? complete.reason;

  const patientName = visit.patientName ?? "Unknown";
  const serviceType = visit.serviceType?.display ?? visit.serviceType?.text ?? null;
  const timeRange = visit.start && visit.end ? `${formatTime(visit.start)} – ${formatTime(visit.end)}` : null;

  const handleComplete = () => {
    if (!visit.visitId || disabledReason || !complete.allowed) return;
    onComplete(visit.visitId);
  };

  return (
    <div className={cn("rounded-md border p-3 space-y-2", isOverdue && "border-destructive bg-destructive/5")}>
      <div className="flex items-center justify-between gap-2">
        <Link
          to="/patients/$patientId/visits/$visitId"
          params={{ patientId: visit.patientId ?? "", visitId: visit.visitId }}
          className="min-w-0 flex items-center gap-1 text-sm font-semibold hover:underline"
          onClick={onClosePanel}
        >
          <span className="truncate">{patientName}</span>
          <SquareArrowOutUpRight className="size-3 shrink-0" />
        </Link>
        <AppointmentStatusBadge status={visit.status} />
      </div>

      <div className="text-muted-foreground space-y-1 text-xs">
        {visit.practitionerId && (
          <p className="flex items-center gap-1.5">
            <UserRoundIcon className="size-3 shrink-0" />
            <PractitionerDisplay practitionerId={visit.practitionerId} fallbackDisplay={visit.practitionerName} />
          </p>
        )}
        {serviceType && (
          <p className="flex items-center gap-1.5">
            <StethoscopeIcon className="size-3 shrink-0" />
            {serviceType}
          </p>
        )}
        {timeRange && (
          <p className="flex items-center gap-1.5">
            <ClockIcon className="size-3 shrink-0" />
            {timeRange}
          </p>
        )}
        {isOverdue && visit.end && (
          <p className="flex items-center gap-1.5 text-destructive font-medium">
            <AlertTriangleIcon className="size-3 shrink-0" />
            Overdue {(() => {
              const end = new Date(visit.end);
              const days = differenceInCalendarDays(now, end);
              if (days === 0) {
                const h = Math.floor(overdueMinutes / 60);
                const m = overdueMinutes % 60;
                if (h === 0) return `${m} Minute${m !== 1 ? "s" : ""}`;
                return `${h} Hour${h !== 1 ? "s" : ""} ${m} Minute${m !== 1 ? "s" : ""}`;
              }
              return `${days} Day${days > 1 ? "s" : ""}`;
            })()}
          </p>
        )}
      </div>

      {disabledReason && <p className="text-muted-foreground text-xs">{disabledReason}</p>}
      <div className="flex gap-2 pt-1">
        <Button
          size="sm"
          className="flex-1 h-7 text-xs"
          onClick={handleComplete}
          disabled={Boolean(disabledReason) || !complete.allowed}
        >
          Complete
        </Button>
        <Button size="sm" variant="destructive" className="flex-1 h-7 text-xs" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
